import React, { useEffect, useState } from 'react';

import { Box, MenuItem, Paper, Select, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Forward10Icon from '@mui/icons-material/Forward10';
import Replay10Icon from '@mui/icons-material/Replay10';
import { Slider } from '@material-ui/core';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FullscreenIcon from '@mui/icons-material/Fullscreen';

import Player from 'video.js/dist/types/player';

import IconButtonWithTooltip from '../../../../components/IconButtonWithTooltip';
import TranslationHelper from '../../../../helpers/TranslationHelper';
import { useStyles } from './Themable.hooks';
import { PLAYBACK_RATES } from '../../../../constants/videoPlayerSettings';
import TimeFormatter from '../../../../helpers/TimeFormatter';
import { syncPlayStateFromAnyPlayer, updateTimeAndDuration } from './helpers';

interface IOwnProps {
    allReady: boolean;
    playerRefs: React.MutableRefObject<{
        [cameraId: string]: Player;
    }>;
    handleFullscreen: () => void;
    fullscreen: boolean;
}

const ControlBar = ({
    allReady,
    playerRefs,
    handleFullscreen,
    fullscreen,
}: IOwnProps) => {
    const [currentTime, setCurrentTime] = useState(0);
    const [maxDuration, setMaxDuration] = useState(0);
    const [currentRate, setCurrentRate] = useState(1);
    const [isPlaying, setIsPlaying] = useState(false);

    const classes = useStyles();

    const playersArray = Object.values(playerRefs.current);

    useEffect(() => {
        const interval = setInterval(() => {
            updateTimeAndDuration(
                Object.values(playerRefs.current),
                setCurrentTime,
                setMaxDuration
            );
            syncPlayStateFromAnyPlayer(
                Object.values(playerRefs.current),
                setIsPlaying
            );
        }, 500);

        return () => clearInterval(interval);
    }, [playerRefs, allReady]);

    const handleTogglePlayPause = () => {
        const players = playersArray;
        if (isPlaying) {
            players.forEach((player) => {
                if (player && !player.isDisposed()) player.pause();
            });
        } else {
            players.forEach((player) => {
                if (player && !player.isDisposed()) player.play();
            });
        }
        setIsPlaying((prev) => !prev);
    };

    const handleSeekAll = (offsetSeconds: number) => {
        playersArray.forEach((player) => {
            const current = player.currentTime();
            current && player.currentTime(current + offsetSeconds);
        });
    };

    const handleSetPlaybackRateAll = (rate: number) => {
        setCurrentRate(rate);
        playersArray.forEach((player) => {
            player.playbackRate(rate);
        });
    };

    const handleSeekAllPlayers = (newTime: number) => {
        playerRefs.current &&
            playersArray.forEach((player) => {
                if (player && !player.isDisposed()) {
                    player.currentTime(newTime);
                }
            });
        setCurrentTime(newTime);
    };

    const formattedTimeDisplay = `${TimeFormatter.secondsToMMSS(
        currentTime
    )} / ${
        maxDuration && maxDuration !== Infinity
            ? TimeFormatter.secondsToMMSS(maxDuration)
            : '--:--'
    }`;

    return (
        <Paper
            elevation={3}
            sx={{
                backgroundColor: '#1e1e1e',
                color: '#fff',
                padding: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                marginTop: 2,
                borderRadius: 1,
            }}
        >
            <IconButtonWithTooltip
                aria-label={TranslationHelper.translate(
                    isPlaying ? 'Pause all' : 'Play all'
                )}
                title={TranslationHelper.translate(
                    isPlaying ? 'Pause all' : 'Play all'
                )}
                onClick={handleTogglePlayPause}
            >
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButtonWithTooltip>
            <IconButtonWithTooltip
                aria-label={TranslationHelper.translate(
                    'Skip backward 10 seconds'
                )}
                title={TranslationHelper.translate('Skip backward 10 seconds')}
                onClick={() => handleSeekAll(-10)}
            >
                <Replay10Icon />
            </IconButtonWithTooltip>
            <IconButtonWithTooltip
                aria-label={TranslationHelper.translate(
                    'Skip forward 10 seconds'
                )}
                title={TranslationHelper.translate('Skip forward 10 seconds')}
                onClick={() => handleSeekAll(10)}
            >
                <Forward10Icon />
            </IconButtonWithTooltip>

            <Select
                size="small"
                value={currentRate}
                onChange={(e) =>
                    handleSetPlaybackRateAll(Number(e.target.value))
                }
                sx={{
                    color: 'white',
                    fontWeight: 'normal',
                    fontSize: '1rem',
                    background: 'transparent',
                    minWidth: 'auto',
                    '& .MuiSelect-icon': {
                        color: 'white',
                    },
                    display: 'flex',
                    flexDirection: 'column',
                }}
                disableUnderline
                IconComponent={() => null}
                variant="standard"
            >
                {PLAYBACK_RATES.map((rate) => (
                    <MenuItem key={rate} value={rate}>
                        {rate}x
                    </MenuItem>
                ))}
            </Select>
            <Box sx={{ width: 300, mx: 2 }}>
                <Typography variant="caption" sx={{ color: 'white' }}>
                    {formattedTimeDisplay}
                </Typography>
                <Slider
                    value={currentTime}
                    min={0}
                    max={maxDuration}
                    step={0.1}
                    onChange={(_, value) =>
                        handleSeekAllPlayers(value as number)
                    }
                    classes={{
                        mark: classes.sliderMark,
                        markLabel: classes.sliderMarkLabel,
                        rail: classes.sliderRail,
                        track: classes.sliderTrack,
                        thumb: classes.sliderThumb,
                        valueLabel: classes.sliderValueLabel,
                    }}
                />
            </Box>
            <IconButtonWithTooltip
                aria-label={TranslationHelper.translate('Fullscreen')}
                title={TranslationHelper.translate('Fullscreen')}
                onClick={handleFullscreen}
            >
                {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButtonWithTooltip>
        </Paper>
    );
};

export default ControlBar;
